import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PlusCircle, Edit2, Trash2, Eye, CheckCircle, FileText, Search, RefreshCw } from 'lucide-react';
import apiService from '../services/api';
import { Blog, PagedResponse } from '../types';

const STATUS_COLORS: Record<string, string> = {
  published: 'bg-green-50 text-green-700',
  draft:     'bg-yellow-50 text-yellow-700',
  archived:  'bg-gray-100 text-gray-500',
};

const CATEGORIES = [
  '', 'Mobile Repair', 'Battery Replacement', 'Screen Repair', 'iPhone',
  'Samsung', 'OnePlus', 'Business', 'Tips & Tricks',
];

const BlogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]           = useState<PagedResponse<Blog> | null>(null);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [catFilter, setCat]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getBlogs(page, 20, search, statusFilter, catFilter);
      if (res.isSuccess) setData(res.entity);
      else toast.error(res.message || 'Failed to load blogs');
    } catch {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, catFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      const res = await apiService.deleteBlog(id);
      if (res.isSuccess) { toast.success('Blog deleted'); load(); }
      else toast.error(res.message || 'Delete failed');
    } catch { toast.error('Delete failed'); }
  };

  const handlePublish = async (id: number) => {
    try {
      const res = await apiService.publishBlog(id);
      if (res.isSuccess) { toast.success('Published! Rebuild Next.js to go live.'); load(); }
      else toast.error(res.message || 'Publish failed');
    } catch { toast.error('Publish failed'); }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await apiService.updateBlogStatus(id, status);
      toast.success(`Status set to ${status}`);
      load();
    } catch { toast.error('Status update failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Blogs</h1>
            <p className="text-sm text-gray-500">{data?.totalCount ?? 0} posts total</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/blogs/create')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
        >
          <PlusCircle className="h-4 w-4" /> New Blog
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search title, slug, author…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={catFilter}
            onChange={e => { setCat(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c || 'All Categories'}</option>)}
          </select>
          <button
            onClick={load}
            className="p-2 border border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : !data?.items?.length ? (
          <div className="p-12 text-center text-gray-400">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No blogs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Author</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Words</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {data.items.map(blog => (
                  <tr key={blog.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-xs text-sm">{blog.title}</div>
                      <div className="text-xs text-gray-400 truncate max-w-xs">{blog.slug}</div>
                      {(blog.isFeatured || blog.isTrending) && (
                        <div className="flex gap-1 mt-1">
                          {blog.isFeatured && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">Featured</span>}
                          {blog.isTrending && <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full font-medium">Trending</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-gray-600">{blog.category}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-500">{blog.author}</td>
                    <td className="px-4 py-3">
                      <select
                        value={blog.status}
                        onChange={e => handleStatusChange(blog.id, e.target.value)}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border-0 cursor-pointer ${STATUS_COLORS[blog.status] || 'bg-gray-100 text-gray-600'}`}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-500">{blog.wordCount}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {blog.status !== 'published' && (
                          <button
                            onClick={() => handlePublish(blog.id)}
                            title="Publish"
                            className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        <a
                          href={`https://www.mintorepairs.com/blogs/blog/${blog.slug}/`}
                          target="_blank"
                          rel="noreferrer"
                          title="View live"
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => navigate(`/blogs/edit/${blog.id}`)}
                          title="Edit"
                          className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(blog.id, blog.title)}
                          title="Delete"
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4 mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>Page {data.page} of {data.totalPages} ({data.totalCount} posts)</span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >Prev</button>
            <button
              disabled={page >= data.totalPages}
              onClick={() => setPage(p => p + 1)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogsPage;
